import React, { FC } from "react";
import { Box, Typography, alpha } from "@mui/material";
import type { LivePlayerEntry } from "./liveDirectoryTypes";
import AutoFitText from "./AutoFitText";

type LiveDirectoryProps = {
  pageEntries: LivePlayerEntry[];
  columns: number;
  empty?: boolean;
  recentLaunchMs: number;
  nowMs: number;
  density?: "comfort" | "cozy" | "compact";
};

const LiveDirectory: FC<LiveDirectoryProps> = ({
  pageEntries,
  columns,
  empty,
  recentLaunchMs,
  nowMs,
  density = "cozy",
}) => {
  const nomMax = density === "comfort" ? 42 : density === "cozy" ? 34 : 28;
  const nomMin = density === "comfort" ? 18 : density === "cozy" ? 15 : 13;
  const prenomMax = density === "comfort" ? 24 : density === "cozy" ? 18 : 15;
  const prenomMin = density === "comfort" ? 14 : 12;
  const tableSize =
    density === "comfort"
      ? { xs: "2.5rem", md: "3.1rem", xl: "3.5rem" }
      : density === "cozy"
        ? { xs: "1.95rem", md: "2.25rem", xl: "2.5rem" }
        : { xs: "1.65rem", md: "1.95rem", xl: "2.15rem" };
  const cardPad =
    density === "comfort"
      ? { xs: 2, md: 2.5, xl: 3 }
      : density === "cozy"
        ? { xs: 1.5, md: 1.75, xl: 2 }
        : { xs: 1.15, md: 1.35, xl: 1.5 };

  if (empty) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "grid",
          placeItems: "center",
          px: 3,
          textAlign: "center",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: '"Syne", sans-serif',
              fontWeight: 800,
              fontSize: { xs: "1.8rem", md: "2.6rem" },
              letterSpacing: -0.5,
              color: "#F4F7FB",
            }}
          >
            Aucun match en cours
          </Typography>
          <Typography
            sx={{
              mt: 1.25,
              color: alpha("#F4F7FB", 0.55),
              fontFamily: '"Outfit", sans-serif',
              fontSize: { xs: "1.1rem", md: "1.35rem" },
            }}
          >
            Les noms et n° de table apparaîtront ici automatiquement
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          md: "repeat(3, minmax(0, 1fr))",
          lg: `repeat(${columns}, minmax(0, 1fr))`,
        },
        gridAutoRows: "1fr",
        gap:
          density === "comfort"
            ? { xs: 1.5, md: 2, xl: 2.5 }
            : density === "cozy"
              ? { xs: 1.15, md: 1.35, xl: 1.6 }
              : { xs: 1, md: 1.15, xl: 1.35 },
        alignContent: "stretch",
        // filter: none en fin d’anim — blur(0) persistant (fill-mode both)
        // crée un containing block qui clippe le overflow des noms.
        "@keyframes liveCardIn": {
          from: {
            opacity: 0,
            transform: "translateY(22px) scale(0.94)",
            filter: "blur(6px)",
          },
          to: {
            opacity: 1,
            transform: "translateY(0) scale(1)",
            filter: "none",
          },
        },
        // Encadré scintillant (bordure)
        "@keyframes liveRecentGlow": {
          "0%, 100%": {
            boxShadow: `0 0 0 1px ${alpha("#FF8A3D", 0.45)}, 0 12px 36px ${alpha("#FF8A3D", 0.22)}, inset 0 1px 0 ${alpha("#fff", 0.14)}`,
          },
          "50%": {
            boxShadow: `0 0 0 2px ${alpha("#FF8A3D", 0.95)}, 0 22px 56px ${alpha("#FF8A3D", 0.42)}, inset 0 1px 0 ${alpha("#fff", 0.2)}`,
          },
        },
        // Intérieur : fondu bleu → orange ambré (≠ chip #FF8A3D)
        "@keyframes liveRecentFill": {
          "0%, 100%": { opacity: 0 },
          "50%": { opacity: 1 },
        },
        "@keyframes liveNouveauPulse": {
          "0%, 100%": { opacity: 1 },
          "50%": { opacity: 0.82 },
        },
        "@keyframes liveShine": {
          "0%": { transform: "translateX(-120%) skewX(-12deg)", opacity: 0 },
          "35%": { opacity: 1 },
          "100%": { transform: "translateX(220%) skewX(-12deg)", opacity: 0 },
        },
      }}
    >
      {pageEntries.map((entry, index) => {
        const accent = entry.boardColor || "#3D9BE8";
        const isRecent =
          entry.launchedAtMs != null &&
          nowMs - entry.launchedAtMs >= 0 &&
          nowMs - entry.launchedAtMs <= recentLaunchMs;
        const boardTitle = entry.boardLabel || entry.boardName || "";
        /** Orange intérieur distinct du chip NOUVEAU (#FF8A3D). */
        const recentFillOrange = "#D4783A";

        return (
          <Box
            key={entry.key}
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              height: "100%",
              overflow: "hidden",
              borderRadius: 2.75,
              px: cardPad,
              pt: cardPad,
              pb: cardPad,
              // Fond de base toujours « bleu » ; le fondu orange est en overlay
              background: isRecent
                ? `radial-gradient(100% 80% at 100% 0%, ${alpha("#3D9BE8", 0.22)} 0%, transparent 45%),
                   linear-gradient(160deg, ${alpha("#fff", 0.1)} 0%, ${alpha("#0B1F33", 0.92)} 45%, ${alpha("#071018", 0.98)} 100%)`
                : `radial-gradient(100% 80% at 100% 0%, ${alpha(accent, 0.16)} 0%, transparent 45%),
                   linear-gradient(160deg, ${alpha("#fff", 0.1)} 0%, ${alpha("#fff", 0.03)} 45%, ${alpha("#000", 0.28)} 100%)`,
              border: 1,
              borderColor: isRecent
                ? alpha("#FF8A3D", 0.85)
                : alpha("#fff", 0.12),
              boxShadow: isRecent
                ? `0 0 0 1px ${alpha("#FF8A3D", 0.4)}, 0 18px 44px ${alpha("#FF8A3D", 0.22)}, inset 0 1px 0 ${alpha("#fff", 0.14)}`
                : `0 12px 32px ${alpha("#000", 0.32)}, inset 0 1px 0 ${alpha("#fff", 0.07)}`,
              animation: isRecent
                ? "liveCardIn 520ms cubic-bezier(0.22, 1, 0.36, 1) both, liveRecentGlow 2.8s ease-in-out infinite"
                : "liveCardIn 520ms cubic-bezier(0.22, 1, 0.36, 1) both",
              animationDelay: `${index * 28}ms${isRecent ? ", 0s" : ""}`,
              // Reflet entrée de page (cartes normales uniquement)
              "&::before": isRecent
                ? { content: '""', display: "none" }
                : {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(105deg, transparent 42%, ${alpha("#fff", 0.06)} 50%, transparent 58%)`,
                    transform: "translateX(-120%) skewX(-12deg)",
                    animation: "liveShine 700ms ease-out forwards",
                    animationDelay: `${220 + index * 18}ms`,
                    pointerEvents: "none",
                    zIndex: 2,
                  },
              // Filigrane n° table
              "&::after": {
                content: `"${String(entry.tableId)}"`,
                position: "absolute",
                right: -2,
                bottom: 36,
                fontFamily: '"Syne", sans-serif',
                fontWeight: 800,
                fontSize: density === "compact" ? "3.6rem" : "4.6rem",
                lineHeight: 1,
                letterSpacing: "-0.06em",
                color: isRecent
                  ? alpha("#FF8A3D", 0.26)
                  : alpha("#FF8A3D", 0.12),
                pointerEvents: "none",
                userSelect: "none",
                zIndex: 0,
              },
            }}
          >
            {isRecent && (
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "inherit",
                  pointerEvents: "none",
                  zIndex: 0,
                  background: `
                    radial-gradient(120% 90% at 0% 0%, ${alpha(recentFillOrange, 0.55)} 0%, transparent 50%),
                    linear-gradient(155deg, ${alpha(recentFillOrange, 0.42)} 0%, ${alpha("#B85F28", 0.22)} 45%, transparent 100%)
                  `,
                  animation: "liveRecentFill 2.8s ease-in-out infinite",
                }}
              />
            )}

            {/* 1. Table + NOUVEAU */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexShrink: 0,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: 0.75,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontFamily: '"Outfit", sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: 11, md: 12 },
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: alpha("#F4F7FB", 0.5),
                  }}
                >
                  Table
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: '"Syne", sans-serif',
                    fontWeight: 800,
                    fontSize: tableSize,
                    lineHeight: 1,
                    letterSpacing: -1.4,
                    color: "#FF8A3D",
                    textShadow: isRecent
                      ? `0 0 28px ${alpha("#FF8A3D", 0.75)}, 0 0 48px ${alpha("#FF8A3D", 0.35)}`
                      : `0 0 24px ${alpha("#FF8A3D", 0.45)}`,
                  }}
                >
                  {entry.tableId}
                </Typography>
              </Box>

              {isRecent && (
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.45,
                    borderRadius: 999,
                    bgcolor: "#FF8A3D",
                    color: "#1A120C",
                    fontFamily: '"Outfit", sans-serif',
                    fontWeight: 800,
                    fontSize: { xs: 11, md: 12 },
                    letterSpacing: 1,
                    lineHeight: 1.2,
                    boxShadow: `0 0 0 1px ${alpha("#fff", 0.2)}, 0 4px 14px ${alpha("#FF8A3D", 0.45)}`,
                    animation: "liveNouveauPulse 2.2s ease-in-out infinite",
                  }}
                >
                  NOUVEAU
                </Box>
              )}
            </Box>

            {/* 2. Nom + prénom — ne pas shrink (minHeight:0 + center + overflow carte
                clippaient le nom en haut, ne laissant que le prénom visible). */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                flex: "1 0 auto",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                py: 0.75,
                // Pas de filter ici : il clippe le contenu. Halo via text-shadow.
                ...(isRecent
                  ? {
                      "& span": {
                        textShadow: `0 0 18px ${alpha("#FF8A3D", 0.35)}`,
                      },
                    }
                  : {}),
              }}
            >
              <Box sx={{ flexShrink: 0, minWidth: 0 }}>
                <AutoFitText
                  text={entry.nom}
                  maxPx={nomMax}
                  minPx={nomMin}
                  fontFamily='"Syne", sans-serif'
                  fontWeight={800}
                  color="#F4F7FB"
                  letterSpacing={-0.6}
                />
              </Box>
              {entry.prenom ? (
                <Box sx={{ mt: 0.4, flexShrink: 0, minWidth: 0 }}>
                  <AutoFitText
                    text={entry.prenom}
                    maxPx={prenomMax}
                    minPx={prenomMin}
                    fontFamily='"Outfit", sans-serif'
                    fontWeight={600}
                    color={alpha("#F4F7FB", 0.8)}
                    letterSpacing={-0.2}
                  />
                </Box>
              ) : null}
            </Box>

            {/* 3. Bandeau tableau + tour */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                flexShrink: 0,
                alignSelf: "stretch",
                mt: 0.5,
                px: density === "compact" ? 1.1 : 1.25,
                py: density === "compact" ? 0.7 : 0.9,
                borderRadius: 1.75,
                overflow: "hidden",
                background: isRecent
                  ? `linear-gradient(105deg, ${alpha("#FF8A3D", 0.5)} 0%, ${alpha("#0A1A2A", 0.9)} 58%, ${alpha("#061018", 0.95)} 100%)`
                  : `linear-gradient(105deg, ${alpha(accent, 0.48)} 0%, ${alpha("#0A1A2A", 0.9)} 55%, ${alpha("#061018", 0.94)} 100%)`,
                border: `1px solid ${alpha("#fff", isRecent ? 0.22 : 0.16)}`,
                boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.12)}, 0 8px 20px ${alpha("#000", 0.28)}`,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  bgcolor: isRecent ? "#FF8A3D" : accent,
                  boxShadow: `0 0 14px ${alpha(isRecent ? "#FF8A3D" : accent, 0.75)}`,
                },
              }}
            >
              <Typography
                sx={{
                  position: "relative",
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 700,
                  fontSize:
                    density === "compact"
                      ? { xs: 11, md: 12, xl: 13 }
                      : { xs: 12, md: 13, xl: 14 },
                  lineHeight: 1.2,
                  color: "#F7FAFC",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {boardTitle}
              </Typography>
              <Typography
                sx={{
                  position: "relative",
                  mt: 0.2,
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 600,
                  fontSize:
                    density === "compact"
                      ? { xs: 11, md: 12 }
                      : { xs: 12, md: 13 },
                  lineHeight: 1.2,
                  color: alpha("#F7FAFC", 0.84),
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {entry.tourLabel || "—"}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default LiveDirectory;
